<script setup lang="ts">
import FooterLogo from '~/assets/img/header-logo.svg'; // Импорт логотипа футера

import {useWindowSize} from "@vueuse/core";
import {RoleEnum} from "~/types/User";
import { Button } from '~/components/ui/button'
import ButtonLocationSelect from '~/components/custom-ui/ButtonLocationSelect.vue'
import AuthMainElement from '~/components/layouts/AuthMainElement.vue'


 // Хранилище для авторизации
const userStore = useUserStore(); // Хранилище профиля пользователя
const Loader = useIsLoadingStore()
const globalStore = useGlobalStore()
const {width, height} = useWindowSize()
const config = useRuntimeConfig()
const route = useRoute()

const isHidden = computed(() => {
    return width.value && width.value < 1024 && route.name === 'office_chats'
})

const rule_pages = computed(() => {
    return Object.entries(globalStore.rule_pages)
        .filter(([key, item]) => key !== 'jobtoken' && !Array.isArray(item))
        .map(([key, item]) => item)
})
</script>

<template>
  <footer v-if="!isHidden" class="ys-footer pt-8 font-medium mt-10 pb-20 md:pb-8">
    <div class="container">
      <!-- Логотип футера -->
      <div class="mb-8">
        <NuxtLink to="/" class="cursor-pointer block w-fit mx-auto md:mx-0">
          <FooterLogo class="ys-logo"/>
        </NuxtLink>
      </div>
      <!-- Основная навигация в футере -->
      <div class="flex flex-wrap md:flex-nowrap justify-between items-start pb-7 border-b border-gray-200">
        <nav class="flex w-full md:w-auto text-center md:text-left mb-4 md:mb-0 flex-col md:flex-row xl:space-x-36 sm:space-x-8">
          <ul class="font-medium lg:columns-3 gap-20">
            <li class="mb-2">
                <NuxtLink 
                    v-if="!userStore.isAuthenticated"
                    :to="{ name: 'auth' }"
                    class="hover:text-gray-400">
                    <p>{{ $t("Join") }}</p>
                </NuxtLink>

                <!-- Кнопка выхода из аккаунта, если пользователь авторизован -->
                <Button v-else :disabled="Loader.isLoading" class="ys-button"  @click="userStore.logout">
                  <Icon v-if="Loader.isLoading" name="svg-spinners:ring-resize" />
                  <span v-else>{{ $t("Logout") }}</span>
                </Button>
            </li>
            <li class="mb-2">
                <template v-if="userStore.isAuthenticated">
                    <NuxtLink 
                        v-if="userStore.user?.user_role === RoleEnum.CUSTOMER"
                        :to="{ name: 'order_create' }"
                        class="hover:text-gray-400">
                        <p>{{ $t("Create task") }}</p>
                    </NuxtLink>

                    <NuxtLink
                        v-if="userStore.user?.user_role === RoleEnum.MASTER"
                        :to="{ name: 'office_task_search' }"
                        class="hover:text-gray-400">
                        <p>{{ $t("Tasks search") }}</p>
                    </NuxtLink>
                </template>
                <NuxtLink 
                    v-else
                    :to="{ name: 'auth' }"
                    class="hover:text-gray-400">
                    <p>{{ $t("Create task") }}</p>
                </NuxtLink>
            </li>
            <li class="mb-2">
                <NuxtLink to="/help" class="hover:text-gray-600">{{ $t("Help & Support") }}</NuxtLink>
            </li>
            <li class="mb-2">
                <NuxtLink to="/aboutus" class="hover:text-gray-600">{{ $t("About us") }}</NuxtLink>
            </li>
            <li class="mb-2">
                <NuxtLink to="/contacts" class="hover:text-gray-600">{{ $t("Contacts") }}</NuxtLink>
            </li>
            <li class="mb-2">
                <NuxtLink to="https://youragency.ae" class="hover:text-gray-600" external>{{ $t("Visit YourAgency") }}</NuxtLink>
            </li>
          </ul>
        </nav>

        <!-- Иконки приложений для мобильных устройств -->
        <div v-if="config.public.playstoreLink || config.public.appstoreLink" class="flex w-full md:w-fit justify-center gap-5">
            <a :href="config.public.playstoreLink" target="_blank" class="w-fit">
                <NuxtImg class="ys-mobile-logo" src="/home_page/googleplay.svg" />
            </a>
            <a :href="config.public.appstoreLink" target="_blank" class="w-fit">
                <NuxtImg class="ys-mobile-logo" src="/home_page/appstore.svg" />
            </a>
        </div>

      </div>

      <!-- Навигация в нижней части футера -->
      <div class="flex justify-between items-start">
        <nav class="pt-4 md:pt-7 pb-5 w-full">
          <div class="flex flex-wrap md:space-x-8 justify-center md:justify-start items-center">
            <ButtonLocationSelect btn_class="max-h-[36px] text-main" />
            <!-- <LayoutsLanguageSelector class="lg-selector-for-footer mx-auto mb-6 md:mb-0 md:ml-0"/> -->
            <div class="text-gray-400 text-center md:text-left text-sm">
              © {{ new Date().getFullYear() }} YourService
            </div>
             
          </div>
          <div class="flex flex-wrap w-full gap-2 md:gap-6 justify-center md:justify-start mt-4 md:pl-2 " v-if="globalStore.rule_pages">
              <template v-for="(item, key) in rule_pages">
                <NuxtLink v-if="item" class="my-auto w-full md:w-auto hover:text-gray-600 cursor-pointer text-sm font-normal text-gray-400 text-center md:text-left" :to="`/page/${item.documentId}`">
                  {{ item.name}}
                </NuxtLink>
              </template>
             </div>
        </nav>
      </div>
      <!-- Текст с копирайтом -->
      
    </div>
  </footer>
</template>

<style scoped lang="scss">
.ys-mobile-logo {
  width: 125.62px;
  height: 35px;
  box-shadow: 0 1px 2px 0 var(--ys-button-shadow-inset) inset;
  border-radius: 6px;
}


.ys-footer-nav-button {
  @apply my-auto cursor-pointer;
  color: rgb(0, 0, 0);
  background: unset;
  text-decoration: unset;
  border-radius: 8px;
  font-size: 14px;
  height: 36px;
  padding: 8px 12px 8px 12px;
  gap: 8px;
}

.ys-footer-nav-button:hover {
  background: unset;
}

</style>


